import { useCallback, useRef, useState } from 'react'
import { fetchVehicleData } from '../api'
import { useHomeStore } from '../store'

interface VinJob {
  carId: number
  vin: string
}

interface QueueState {
  active: boolean
  completed: number
  total: number
  status: string
  startedAt: number | null
}

const INITIAL: QueueState = { active: false, completed: 0, total: 0, status: '', startedAt: null }

const REQUEST_DELAY_MS = 1100

export function useVinQueue() {
  const [state, setState] = useState<QueueState>(INITIAL)
  const queueRef = useRef<VinJob[]>([])
  const processingRef = useRef(false)
  const cancelledRef = useRef(false)
  const setVehicleGovData = useHomeStore((s) => s.setVehicleGovData)
  const updateVehicle = useHomeStore((s) => s.updateVehicle)

  const process = useCallback(async () => {
    if (processingRef.current) return
    processingRef.current = true
    cancelledRef.current = false
    setState((p) => ({ ...p, active: true, startedAt: Date.now() }))

    while (queueRef.current.length > 0 && !cancelledRef.current) {
      const job = queueRef.current.shift()
      if (!job) break
      setState((p) => ({ ...p, status: `Načítám ${job.vin}…` }))
      try {
        const data = await fetchVehicleData(job.vin)
        const apiStatus = data.Status ?? (data.Data ? 1 : 0)
        const isEmpty = !data.Data || Object.keys(data.Data).length === 0
        const notFound = apiStatus === 0 || isEmpty
        setVehicleGovData(job.carId, { Data: data.Data, Status: apiStatus }, notFound)
        if (data.Data) {
          updateVehicle(job.carId, {
            specifikace:
              (data.Data.Specifikace as string) ||
              (data.Data.VozidloDruh2R as string) ||
              (data.Data.VozidloDruh2 as string) ||
              (data.Data.DruhVozidla2R as string) ||
              (data.Data.DruhVozidla2 as string) ||
              '',
          })
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'chyba'
        setState((p) => ({ ...p, status: `${job.vin}: ${msg}` }))
      } finally {
        setState((p) => ({ ...p, completed: p.completed + 1 }))
      }
      if (queueRef.current.length > 0) {
        await new Promise((res) => setTimeout(res, REQUEST_DELAY_MS))
      }
    }

    processingRef.current = false
    setState((p) => ({ ...p, active: false, status: cancelledRef.current ? 'Zrušeno' : 'Hotovo' }))
  }, [setVehicleGovData, updateVehicle])

  const enqueue = useCallback(
    (jobs: VinJob[]) => {
      if (jobs.length === 0) return
      queueRef.current.push(...jobs)
      setState((p) => ({
        ...p,
        active: true,
        total: p.total + jobs.length,
        startedAt: p.startedAt ?? Date.now(),
      }))
      void process()
    },
    [process],
  )

  const cancel = useCallback(() => {
    cancelledRef.current = true
    queueRef.current = []
  }, [])

  return { state, enqueue, cancel }
}
